/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListSkillSuggestionFeedbackRequest,
  ListSkillSuggestionFeedbackSecurity,
} from "../models/operations/listskillsuggestionfeedback.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildSkillSuggestionFeedbackQuery,
  prefetchSkillSuggestionFeedback,
  queryKeySkillSuggestionFeedback,
  SkillSuggestionFeedbackQueryData,
} from "./skillSuggestionFeedback.core.js";
export {
  buildSkillSuggestionFeedbackQuery,
  prefetchSkillSuggestionFeedback,
  queryKeySkillSuggestionFeedback,
  type SkillSuggestionFeedbackQueryData,
};

export type SkillSuggestionFeedbackQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listSuggestionFeedback skills
 *
 * @remarks
 * List the agent feedback cited as the reason for one proposed change, newest first.
 */
export function useSkillSuggestionFeedback(
  request: ListSkillSuggestionFeedbackRequest,
  security?: ListSkillSuggestionFeedbackSecurity | undefined,
  options?: QueryHookOptions<
    SkillSuggestionFeedbackQueryData,
    SkillSuggestionFeedbackQueryError
  >,
): UseQueryResult<
  SkillSuggestionFeedbackQueryData,
  SkillSuggestionFeedbackQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildSkillSuggestionFeedbackQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listSuggestionFeedback skills
 *
 * @remarks
 * List the agent feedback cited as the reason for one proposed change, newest first.
 */
export function useSkillSuggestionFeedbackSuspense(
  request: ListSkillSuggestionFeedbackRequest,
  security?: ListSkillSuggestionFeedbackSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    SkillSuggestionFeedbackQueryData,
    SkillSuggestionFeedbackQueryError
  >,
): UseSuspenseQueryResult<
  SkillSuggestionFeedbackQueryData,
  SkillSuggestionFeedbackQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildSkillSuggestionFeedbackQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setSkillSuggestionFeedbackData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      id: string;
      limit?: number | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: SkillSuggestionFeedbackQueryData,
): SkillSuggestionFeedbackQueryData | undefined {
  const key = queryKeySkillSuggestionFeedback(...queryKeyBase);

  return client.setQueryData<SkillSuggestionFeedbackQueryData>(key, data);
}

export function invalidateSkillSuggestionFeedback(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      id: string;
      limit?: number | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "skills",
      "listSuggestionFeedback",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllSkillSuggestionFeedback(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "skills", "listSuggestionFeedback"],
  });
}
